import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCheck,
  faCheckDouble,
  faUserPlus,
  faExternalLinkAlt,
  faBellSlash,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import {
  beginNotificationPermissionRequest,
  getNotificationPermission,
  registerServiceWorker,
  subscribeAdminToPush,
} from '../utils/pushNotifications';
import '../styles/notifications.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
}

export default function NotificationBell() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('unread');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [pushStatus, setPushStatus] = useState(getNotificationPermission());
  const [pushRegistered, setPushRegistered] = useState(
    () => localStorage.getItem('unilink_push_subscribed') === '1'
  );
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  const panelRef = useRef(null);
  const openRef = useRef(open);
  const filterRef = useRef(filter);

  openRef.current = open;
  filterRef.current = filter;

  const role = String(user?.role || '').toLowerCase();
  const canUseAlerts = role === 'admin' || role === 'counselor';

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  const syncPushFromServer = useCallback(async () => {
    if (!token || !canUseAlerts) return;
    if (getNotificationPermission() !== 'granted') {
      setPushStatus(getNotificationPermission());
      return;
    }
    const result = await subscribeAdminToPush(() => headers());
    setPushStatus(getNotificationPermission());
    if (result.ok) {
      setPushRegistered(true);
      setPushMessage('Desktop alerts enabled');
    } else if (result.reason === 'save_failed' || result.reason === 'vapid') {
      setPushRegistered(false);
      setPushMessage('Could not register this browser for push. Try Enable again.');
    }
  }, [token, canUseAlerts, headers]);

  const fetchUnreadCount = useCallback(async () => {
    if (!token || !canUseAlerts) return;
    try {
      const res = await fetch(`${API_URL}/admin-alerts/unread-count`, {
        headers: headers(),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setUnreadCount(Number(data.unreadCount) || 0);
        setLoadError('');
      } else {
        setLoadError(data.message || `Failed to load alerts (${res.status})`);
      }
    } catch (err) {
      setLoadError(err.message || 'Network error loading alerts');
    }
  }, [token, canUseAlerts, headers]);

  const fetchNotifications = useCallback(async (nextFilter) => {
    if (!token || !canUseAlerts) return;
    const activeFilter = nextFilter || filterRef.current;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/admin-alerts?filter=${encodeURIComponent(activeFilter)}&limit=50`,
        { headers: headers() }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
        if (typeof data.unreadCount === 'number') {
          setUnreadCount(data.unreadCount);
        }
        setLoadError('');
      } else {
        setLoadError(data.message || `Failed to load alerts (${res.status})`);
      }
    } catch (err) {
      setLoadError(err.message || 'Network error loading alerts');
    } finally {
      setLoading(false);
    }
  }, [token, canUseAlerts, headers]);

  useEffect(() => {
    if (!canUseAlerts || !token) return undefined;

    fetchUnreadCount();
    const pollId = setInterval(fetchUnreadCount, 8000);

    registerServiceWorker().then(() => {
      syncPushFromServer().catch(() => {});
    });

    const onMessage = (event) => {
      if (event.data?.type === 'PUSH_RECEIVED') {
        fetchUnreadCount();
        if (openRef.current) fetchNotifications(filterRef.current);
      }
      if (event.data?.type === 'NAVIGATE' && event.data.url) {
        try {
          const path = new URL(event.data.url, window.location.origin).pathname;
          navigate(path);
        } catch {
          navigate(event.data.url);
        }
      }
    };

    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', onMessage);
    }

    return () => {
      clearInterval(pollId);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', onMessage);
      }
    };
  }, [canUseAlerts, token, fetchUnreadCount, fetchNotifications, syncPushFromServer, navigate]);

  useEffect(() => {
    if (open) fetchNotifications(filter);
  }, [open, filter, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  if (!canUseAlerts) return null;

  const handleEnablePush = async (e) => {
    e?.stopPropagation?.();
    setPushBusy(true);
    setPushMessage('');
    // Must start permission request inside this click handler
    const permissionPromise = beginNotificationPermissionRequest();
    try {
      const result = await subscribeAdminToPush(() => headers(), permissionPromise);
      setPushStatus(getNotificationPermission());
      if (result.ok) {
        setPushRegistered(true);
        setPushMessage('Desktop alerts enabled for this browser');
      } else if (result.reason === 'denied') {
        setPushRegistered(false);
        setPushMessage('Permission blocked. Allow notifications for this site in the browser address bar, then click Enable again.');
      } else if (result.reason === 'default') {
        setPushRegistered(false);
        setPushMessage('Permission was not granted. Click Enable again and choose Allow.');
      } else {
        setPushRegistered(false);
        setPushMessage(`Could not enable push (${result.reason}). Check console for details.`);
      }
    } catch (err) {
      setPushMessage(err.message || 'Failed to enable push');
    } finally {
      setPushBusy(false);
    }
  };

  const handleMarkRead = async (id, e) => {
    e?.stopPropagation?.();
    try {
      const res = await fetch(`${API_URL}/admin-alerts/${id}/read`, {
        method: 'POST',
        headers: headers(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (typeof data.unreadCount === 'number') setUnreadCount(data.unreadCount);
        else setUnreadCount((c) => Math.max(0, c - 1));
        if (filterRef.current === 'unread') {
          setNotifications((prev) => prev.filter((n) => n.id !== id));
        } else {
          setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
          );
        }
      }
    } catch {
      /* ignore */
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const res = await fetch(`${API_URL}/admin-alerts/read-all`, {
        method: 'POST',
        headers: headers(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setUnreadCount(0);
        if (filterRef.current === 'unread') setNotifications([]);
        else setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      }
    } catch {
      /* ignore */
    }
  };

  const handleOpenNotification = async (n) => {
    if (!n.is_read) {
      await handleMarkRead(n.id);
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);
  const needsPushSetup = pushStatus !== 'granted' || !pushRegistered;

  return (
    <div className="notif-bell-wrap" ref={panelRef}>
      <button
        type="button"
        className={`notif-bell-btn ${open ? 'open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        title="Notifications"
        aria-expanded={open}
      >
        <FontAwesomeIcon icon={faBell} />
        {unreadCount > 0 && (
          <span className="notif-bell-badge">{badgeLabel}</span>
        )}
      </button>

      {open && (
        <div className="notif-dropdown" role="dialog" aria-label="Notifications">
          <div className="notif-dropdown-header">
            <div>
              <div className="notif-dropdown-title-row">
                <h3>Notifications</h3>
                {unreadCount > 0 && (
                  <span className="notif-count-pill">{unreadCount}</span>
                )}
              </div>
              <p className="notif-dropdown-subtitle">
                {unreadCount === 0
                  ? 'No unread notifications'
                  : `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`}
              </p>
            </div>
            <button
              type="button"
              className="notif-mark-all-btn"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
              title="Mark all as read"
            >
              <FontAwesomeIcon icon={faCheckDouble} /> All read
            </button>
          </div>

          {needsPushSetup && (
            <div className="notif-push-banner">
              <div className="notif-push-banner-text">
                <FontAwesomeIcon icon={faBellSlash} />
                <span>
                  {pushStatus === 'granted'
                    ? 'Finish setup to receive desktop popups (View Lead / Dismiss).'
                    : 'Enable desktop push alerts for new leads.'}
                </span>
              </div>
              <button
                type="button"
                className="notif-enable-push-btn"
                onClick={handleEnablePush}
                disabled={pushBusy}
              >
                {pushBusy ? 'Enabling…' : 'Enable'}
              </button>
              {pushMessage && <div className="notif-push-msg">{pushMessage}</div>}
            </div>
          )}

          {!needsPushSetup && pushMessage && (
            <div className="notif-push-banner success">
              <div className="notif-push-msg">{pushMessage}</div>
            </div>
          )}

          <div className="notif-tabs">
            {['unread', 'read', 'all'].map((tab) => (
              <button
                key={tab}
                type="button"
                className={`notif-tab ${filter === tab ? 'active' : ''}`}
                onClick={() => setFilter(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="notif-list">
            {loadError && (
              <div className="notif-empty notif-error">{loadError}</div>
            )}
            {loading && notifications.length === 0 && !loadError && (
              <div className="notif-empty">Loading…</div>
            )}
            {!loading && !loadError && notifications.length === 0 && (
              <div className="notif-empty">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
              </div>
            )}
            {notifications.map((n) => (
              <div
                key={n.id}
                className={`notif-item ${n.is_read ? 'read' : 'unread'}`}
                onClick={() => handleOpenNotification(n)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleOpenNotification(n);
                }}
              >
                {!n.is_read && <span className="notif-unread-dot" />}
                <div className="notif-item-icon">
                  <FontAwesomeIcon icon={faUserPlus} />
                </div>
                <div className="notif-item-body">
                  <div className="notif-item-title">{n.title}</div>
                  <div className="notif-item-text">{n.body}</div>
                  <div className="notif-item-meta">
                    <span>{timeAgo(n.created_at)}</span>
                    {!n.is_read && <span className="notif-new-badge">NEW</span>}
                  </div>
                </div>
                <div className="notif-item-actions" onClick={(e) => e.stopPropagation()}>
                  {n.link && (
                    <button
                      type="button"
                      className="notif-action-btn"
                      title="View lead"
                      onClick={() => handleOpenNotification(n)}
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </button>
                  )}
                  {!n.is_read && (
                    <button
                      type="button"
                      className="notif-action-btn mark-read"
                      title="Mark as read"
                      onClick={(e) => handleMarkRead(n.id, e)}
                    >
                      <FontAwesomeIcon icon={faCheck} /> Mark read
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
